import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { Link } from "@faire/slate/components/link";
import type { ReactNode } from "react";

interface IncentiveTakeoverProps {
  onDismiss: () => void;
  onOpenTerms: () => void;
  onStartShopping: () => void;
}

export const IncentiveTakeover = ({
  onDismiss,
  onOpenTerms,
  onStartShopping,
}: IncentiveTakeoverProps): ReactNode => {
  return (
    <aside
      aria-label="First order welcome offer"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 h-[11.25rem] md:inset-x-auto md:left-0 md:h-[19vh] md:w-[56vw]"
    >
      <MobileOfferWave />
      <OfferWave />

      <div className="pointer-events-auto absolute right-(--slate-spacing-element-xxlarge) bottom-(--slate-spacing-element-xxlarge) left-(--slate-spacing-element-xxlarge) flex flex-col gap-(--slate-spacing-element-medium) md:right-auto md:bottom-(--slate-spacing-section-small) md:left-(--slate-spacing-layout-edge-desktop-default) md:w-[min(36rem,38vw)]">
        <div className="flex flex-col gap-(--slate-spacing-element-xsmall) md:hidden">
          <p className="type-fs-subheading-medium text-fs-text-primary">
            Get 50% off your first order
          </p>
          <p className="type-fs-paragraph text-fs-text-primary">
            Up to $100 · Ends Aug 12 ·{" "}
            <Link
              as="button"
              className="type-fs-paragraph text-fs-text-primary underline"
              onClick={onOpenTerms}
            >
              See terms
            </Link>
          </p>
        </div>
        <div className="hidden flex-col gap-(--slate-spacing-element-xsmall) md:flex">
          <p className="type-fs-subheading-medium text-fs-text-primary">
            7 days left to get 50% off your first order—up to $100
          </p>
          <p className="type-fs-paragraph text-fs-text-primary">
            Expires August 12 at 11:59 PM EDT.{" "}
            <Link
              as="button"
              className="type-fs-paragraph text-fs-text-primary underline"
              onClick={onOpenTerms}
            >
              See terms
            </Link>
          </p>
        </div>
        <div className="w-full md:hidden">
          <Button fullWidth onClick={onStartShopping}>
            Start shopping
          </Button>
        </div>
        <div className="hidden w-fit md:block">
          <Button onClick={onStartShopping}>Start shopping</Button>
        </div>
      </div>

      <div className="pointer-events-auto absolute top-(--slate-spacing-element-large) right-(--slate-spacing-element-large) md:top-auto md:right-[22%] md:bottom-[6%]">
        <IconButton
          aria-label="Dismiss welcome offer"
          Icon={Close}
          onClick={onDismiss}
          shape="circle"
          size="xSmall"
          variant="primary"
        />
      </div>
    </aside>
  );
};

const MobileOfferWave = (): ReactNode => (
  <svg
    aria-hidden="true"
    className="text-fs-expressive-surface-yellow pointer-events-none absolute inset-0 h-full w-full md:hidden"
    preserveAspectRatio="none"
    viewBox="0 0 390 180"
  >
    <path
      d="M0 22C65 7 132 5 202 12C272 20 334 5 390 12V180H0V22Z"
      fill="currentColor"
    />
  </svg>
);

const OfferWave = (): ReactNode => (
  <svg
    aria-hidden="true"
    className="text-fs-expressive-surface-yellow pointer-events-none absolute inset-0 hidden h-full w-full md:block"
    preserveAspectRatio="none"
    viewBox="0 0 1147 292"
  >
    <path
      d="M0 48C84 24 142 2 213 8C386 16 578 83 729 148C873 210 1012 270 1147 292V292H0V48Z"
      fill="currentColor"
    />
  </svg>
);
